import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Injector, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatTabsModule } from "@angular/material/tabs";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatDividerModule } from "@angular/material/divider";
import { MatCardModule } from "@angular/material/card";
import { CommonModule } from "@angular/common";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { of, timer } from "rxjs";
import { catchError } from "rxjs/operators";
import { FileReaderService } from "../../../viewer-components/services/readers/file-reader.service";
import { GeoService } from "../../../viewer-components/services/internal-services/geo.service";
import { SearchRequestService } from "../../services/search/requests/search-request.service";
import {
  GeofencingDialogConfig,
  GeofencingDialogResult,
  GeometryInputMethod,
  GeometryType,
  IGeofencingArea,
  IGeofencingLayer,
  IGeofencingSearchValidation,
  MAX_GEOFENCING_AREA_SQUARE_KILOMETERS,
  MapSelectionRequest,
  MapSelectionType,
} from "../../model";
import {MatProgressBar} from "@angular/material/progress-bar";
import { GeofencingValidationService } from "../../services/search/validation/geofencing-validation.service";

/**
 * Interface for processed geometry data
 */
interface ProcessedGeometry {
  isValid: boolean;
  isPolygon: boolean;
  wkt: string;
  error?: string;
  displayWkt?: string;
  suggestedRadius?: number;
  isCircleGeometry?: boolean; // Flag to indicate geometry from circle tool
}

@Component({
  selector: "app-search-geofencing-dialog",

  standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatTabsModule,
        MatFormFieldModule,
        MatInputModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        MatDividerModule,
        MatCardModule,
        ReactiveFormsModule,
        MatProgressBar,
    ],
  templateUrl: "./search-geofencing-dialog.component.html",
  styleUrls: ["./search-geofencing-dialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchGeofencingDialogComponent implements OnInit {
  // Constants

  private readonly DEFAULT_RADIUS_METERS = 1000;

  private readonly DEFAULT_MAX_FILE_SIZE = 5242880; // 5MB

  private readonly DEFAULT_ALLOWED_TYPES = [".wkt", ".geojson", ".kml", ".kmz", ".zip", ".csv"];

  /**
   * Human-readable label for each supported file extension, used to build
   * {@link fileUploadDescription}.
   */
  private readonly FILE_TYPE_LABELS: Record<string, string> = {
    ".wkt": "WKT",
    ".geojson": "GeoJSON",
    ".kml": "KML",
    ".kmz": "KMZ",
    ".zip": "Shapefile",
    ".csv": "CSV",
  };

  /**
   * Max time the dialog waits for the embeddings cache warm-up before closing anyway.
   * The warm-up is a best-effort optimization, not a requirement to apply the filter.
   */
  private readonly APPLY_MAX_WAIT_MS = 2000;

  /**
   * Dialog title
   */
  public title = "Configure Geofencing";

  /**
   * Dialog description
   */
  public description = "Define a geographical area to filter your search results.";

  /**
   * Currently selected tab index
   */
  public selectedTabIndex = 0;

  /**
   * Text validation form
   */
  public textForm: FormGroup;

  /**
   * Radius configuration form
   */
  public radiusForm: FormGroup;

  /**
   * Currently uploaded file
   */
  public uploadedFile: File | null = null;

  /**
   * File processing state
   */
  public fileProcessingState: "idle" | "processing" | "success" | "error" = "idle";

  /**
   * Drag and drop state
   */
  public isDragOver = false;

  /**
   * Text validation state
   */
  public isValidatingText = false;

  /**
   * Selected map tool
   */
  public selectedMapTool: MapSelectionType = null;

  /**
   * Processed geometry per tab (isolated state)
   */
  public fileGeometry: ProcessedGeometry | null = null;

  public textGeometry: ProcessedGeometry | null = null;

  public mapGeometry: ProcessedGeometry | null = null;

  public layerGeometry: ProcessedGeometry | null = null;

  /**
   * Currently selected layer ID in the Layer on Map tab
   */
  public selectedLayerId: string | null = null;

  /**
   * General processing state
   */
  public isProcessing = false;

  /**
   * Error messages
   */
  public fileError: string | null = null;

  public globalError: string | null = null;

  MapSelectionType = MapSelectionType;

  /**
   * Description of the supported file types, e.g. "Upload a file (GeoJSON, KML) or a CSV of
   * points.". Built once from {@link acceptedFileTypes} in the constructor so the template
   * reads a plain field instead of recomputing it on every change-detection pass.
   */
  public readonly fileUploadDescription: string;

  /**
   * Configuration
   */
  private config: GeofencingDialogConfig;

  /**
   * Cache key (`wkt|radius`) for the last measured geometry, so the template can read the
   * area through a getter without re-parsing the WKT on every change-detection pass.
   */
  private measuredGeometryKey: string | null = null;

  /**
   * Cached validation for {@link measuredGeometryKey}.
   */
  private measuredGeometryValidation: IGeofencingSearchValidation = { valid: false, reason: null, area: null };

  /**
   * Whether map selection is enabled based on configuration
   */
  get canUseMapSelection(): boolean {
    return this.config.canUseMapSelection !== false; // Default to true if not specified
  }

  /**
   * Whether the embeddings search area limit applies to this dialog instance. Defaults to
   * true so the header/settings geofencing flow is unchanged; callers using this dialog for an
   * unrelated area (e.g. custom-elements detection) opt out via config.
   */
  get enforceAreaLimit(): boolean {
    return this.config.enforceEmbeddingsAreaLimit !== false;
  }

  constructor(
    private dialogRef: MatDialogRef<SearchGeofencingDialogComponent>,
    private cdr: ChangeDetectorRef,
    private fileReaderService: FileReaderService,
    private searchRequestService: SearchRequestService,
    @Inject(MAT_DIALOG_DATA) private dialogData: GeofencingDialogConfig | null,
  ) {
    this.config = {
      maxFileSize: this.DEFAULT_MAX_FILE_SIZE,
      allowedFileTypes: this.DEFAULT_ALLOWED_TYPES,
      ...this.dialogData,
    };

    this.fileUploadDescription = this.buildFileUploadDescription(this.config.allowedFileTypes || this.DEFAULT_ALLOWED_TYPES);

    // Initialize forms
    this.textForm = new FormGroup({
      geometryText: new FormControl("", [Validators.required, this.createGeometryValidator]),
    });

    this.radiusForm = new FormGroup({
      radius: new FormControl(this.DEFAULT_RADIUS_METERS, [Validators.required, Validators.min(1)]),
    });
  }

  ngOnInit(): void {
    this.initializeFromConfig();
  }

  /**
   * Ordered list of GeometryInputMethod values that are currently rendered
   * as tabs, in the SAME order as the template. Mirrors the *ngIf / @if
   * conditions on each <mat-tab> so the visible index can be translated to
   * a method regardless of which tabs are hidden.
   */
  get visibleTabs(): GeometryInputMethod[] {
    const tabs: GeometryInputMethod[] = [];
    if (this.canUseMapSelection) tabs.push(GeometryInputMethod.MAP);
    tabs.push(GeometryInputMethod.FILE);
    tabs.push(GeometryInputMethod.TEXT);
    if (this.availableLayers.length > 0) tabs.push(GeometryInputMethod.LAYER);
    return tabs;
  }

  /**
   * Get current input method based on selected tab (visibility-aware).
   */
  get currentInputMethod(): GeometryInputMethod {
    return this.visibleTabs[this.selectedTabIndex] ?? GeometryInputMethod.FILE;
  }

  /** True when the currently active tab is the map-selection tab. */
  get isMapTab(): boolean {
    return this.currentInputMethod === GeometryInputMethod.MAP;
  }

  /**
   * Returns the list of user layers available for geofencing
   */
  get availableLayers(): IGeofencingLayer[] {
    return this.config.availableLayers || [];
  }

  /**
   * Get formatted max file size
   */
  get maxFileSizeFormatted(): string {
    const size = this.config.maxFileSize || this.DEFAULT_MAX_FILE_SIZE;
    return size >= 1024 * 1024 ? `${(size / (1024 * 1024)).toFixed(1)}MB` : `${Math.round(size / 1024)}KB`;
  }

  /**
   * Get accepted file types string
   */
  get acceptedFileTypes(): string {
    return (this.config.allowedFileTypes || this.DEFAULT_ALLOWED_TYPES).join(",");
  }

  /**
   * Builds the human-readable description of supported file types for the given allowed
   * extensions, e.g. "Upload a file (GeoJSON, KML) or a CSV of points.". CSV is called out
   * separately ("or a CSV of points") to match the original copy's phrasing.
   */
  private buildFileUploadDescription(allowedTypes: string[]): string {
    const hasCsv = allowedTypes.includes(".csv");
    const otherLabels = allowedTypes.filter((type) => type !== ".csv").map((type) => this.FILE_TYPE_LABELS[type] || type.replace(".", "").toUpperCase());

    if (otherLabels.length > 0 && hasCsv) {
      return `Upload a file (${otherLabels.join(", ")}) or a CSV of points.`;
    }
    if (otherLabels.length > 0) {
      return `Upload a file (${otherLabels.join(", ")}).`;
    }
    if (hasCsv) {
      return "Upload a CSV of points.";
    }
    return "Upload a file.";
  }

  /**
   * Handle geometry text changes with automatic validation
   */
  public onGeometryTextChange(): void {
    const text = this.textForm.value.geometryText?.trim();
    if (text) {
      // Validate immediately - automatic validation
      this.validateGeometryText();
    } else {
      this.textGeometry = null;
      this.cdr.detectChanges();
    }
  }

  /**
   * Static helper method to open the geofencing dialog.
   */
  static open(
    dialog: MatDialog,
    config?: GeofencingDialogConfig,
    injector?: Injector,
  ): MatDialogRef<SearchGeofencingDialogComponent, GeofencingDialogResult> {
    const dialogRef = dialog.open(SearchGeofencingDialogComponent, {
      panelClass: "medium-panel-dialog",
      disableClose: false,
      hasBackdrop: true,
      data: config,
      injector,
    });

    return dialogRef;
  }

  /**
   * Initialize component from configuration
   */
  private initializeFromConfig(): void {
    if (this.config.title) {
      this.title = this.config.title;
    }

    if (this.config.description) {
      this.description = this.config.description;
    }
    if (this.config.location && this.config.radiusMeters) {
      this.selectedTabIndex = 0; // Select on Map tab

      this.mapGeometry = {
        isValid: true,
        isCircleGeometry: true,
        isPolygon: false,
        wkt: this.config.location,
      };

      this.radiusForm.patchValue({ radius: this.config.radiusMeters });
      this.radiusForm.get("radius").markAsTouched();
      this.radiusForm.get("radius").updateValueAndValidity();
    }
    if (this.config.wkt) {
      this.selectedTabIndex = 0; // Select on Map tab
      // Store the WKT in mapGeometry instead of textForm
      this.processGeometryText(this.config.wkt).then((result) => {
        this.mapGeometry = result;

        if (result.isCircleGeometry) {
          this.radiusForm.patchValue({ radius: result.suggestedRadius });
          this.radiusForm.get("radius").markAsTouched();
          this.radiusForm.get("radius").updateValueAndValidity();
        }
      });
    }

    // Set initial method tab only if no preloaded data
    if (!this.config.wkt && this.config.initialMethod) {
      switch (this.config.initialMethod) {
        case GeometryInputMethod.FILE:
          this.selectedTabIndex = 0;
          break;
        case GeometryInputMethod.TEXT:
          this.selectedTabIndex = 1;
          break;
        case GeometryInputMethod.MAP:
          this.selectedTabIndex = 2;
          break;
      }
    }
    this.cdr.detectChanges();
  }

  /**
   * Handle tab change
   */
  public onTabChange(index: number): void {
    this.selectedTabIndex = index;
    this.clearErrors();
    this.cdr.detectChanges();
  }

  /**
   * Create geometry validator
   */
  private createGeometryValidator() {
    return (control: any) => {
      const value = (control.value || "").trim();
      if (!value) return null;

      // Try WKT first
      try {
        const featureCollection = GeoService.wktToFeatureCollection(value, false);
        const errors = GeoService.validateGeoJSON(featureCollection);
        if (errors.length === 0) return null;
      } catch {
        // Continue to try GeoJSON
      }

      // Try GeoJSON
      try {
        const parsed = JSON.parse(value);
        const errors = GeoService.validateGeoJSON(parsed);
        if (errors.length === 0) return null;
      } catch {
        // Both failed
      }

      return { invalidText: true };
    };
  }

  /**
   * Validate geometry text
   */
  public async validateGeometryText(): Promise<void> {
    const geometryText = this.textForm.get("geometryText")?.value?.trim();
    if (!geometryText) {
      return;
    }

    this.isValidatingText = true;
    this.cdr.detectChanges();

    try {
      const result = await this.processGeometryText(geometryText);

      if (result.isValid) {
        this.textGeometry = result; // Store in text-specific property
        if (result.isCircleGeometry && result.suggestedRadius) {
          this.radiusForm.patchValue({ radius: result.suggestedRadius });
          this.radiusForm.get("radius").markAsTouched();
          this.radiusForm.get("radius").updateValueAndValidity();
        }
      } else {
        // Store invalid result in textGeometry as well
        this.textGeometry = result;
      }
    } catch (error) {
      // Create error result
      this.textGeometry = {
        isValid: false,
        isPolygon: false,
        isCircleGeometry: false,
        wkt: "",
        error: "Failed to process geometry",
      };
    } finally {
      this.isValidatingText = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Process geometry text (WKT or GeoJSON) - Enhanced version with GEOMETRYCOLLECTION support
   */
  private async processGeometryText(text: string): Promise<ProcessedGeometry> {
    // Detect if input is WKT or GeoJSON
    const trimmedText = text.trim();

    if (this.isWKT(trimmedText)) {
      // Handle GEOMETRYCOLLECTION (circle tool result)
      if (trimmedText.startsWith("GEOMETRYCOLLECTION")) {
        return this.processGeometryCollection(trimmedText);
      }
      if (trimmedText.startsWith("POLYGON") || trimmedText.startsWith("MULTIPOLYGON")) {
        return {
          isValid: true,
          isCircleGeometry: false,
          isPolygon: true,
          wkt: trimmedText,
        };
      }
      if (trimmedText.startsWith("MULTIPOINT")) {
        return {
          isValid: true,
          isCircleGeometry: false,
          isPolygon: true,
          wkt: trimmedText,
        };
      }
      if (trimmedText.startsWith("POINT")) {
        return {
          isValid: true,
          isCircleGeometry: false,
          isPolygon: false,
          wkt: trimmedText,
        };
      }
      // Handle simple geometries
    } else {
      try {
        const geojson = JSON.parse(trimmedText);
        let wkt;
        if (geojson.features) {
          wkt = GeoService.geoJSONFeaturesToWkt(geojson.features, 4326, 4326); // input always geographic coordinates
        } else {
          wkt = GeoService.geoJSONFeatureToWKT(geojson);
        }
        if (this.isWKT(wkt)) {
          return {
            isValid: true,
            isPolygon: true,
            wkt: wkt,
            isCircleGeometry: false,
          };
        }
      } catch (error) {
        console.log(error.message);
      }
    }
    return {
      isValid: false,
      isCircleGeometry: false,
      isPolygon: false,
      wkt: "",
      error: "Not supported",
    };
  }

  /**
   * Process GEOMETRYCOLLECTION from circle tool
   * Contains: POLYGON (circle), POINT (center), LINESTRING (radius)
   */
  private processGeometryCollection(wkt: string): ProcessedGeometry {
    try {
      // Extract LINESTRING for radius calculation
      const lineStringMatch = wkt.match(/LINESTRING\s*\(([^)]+)\)/i);
      if (!lineStringMatch) {
        return {
          isValid: false,
          isCircleGeometry: false,
          isPolygon: false,
          wkt: "",
          error: "LINESTRING not found in GEOMETRYCOLLECTION",
        };
      }

      // Extract POINT for center
      const pointMatch = wkt.match(/POINT\s*\(([^)]+)\)/i);
      if (!pointMatch) {
        return {
          isValid: false,
          isCircleGeometry: false,
          isPolygon: false,
          wkt: "",
          error: "POINT not found in GEOMETRYCOLLECTION",
        };
      }

      // Calculate radius from LINESTRING
      const radiusInMeters = this.calculateRadiusFromLineString(lineStringMatch[1]);

      if (radiusInMeters === null) {
        return {
          isValid: false,
          isCircleGeometry: false,
          isPolygon: false,
          wkt: "",
          error: "Could not calculate radius from LINESTRING",
        };
      }

      // Extract center point coordinates for WKT
      const centerWkt = `POINT(${pointMatch[1]})`;

      return {
        isValid: true,
        isPolygon: false,
        wkt: centerWkt,
        suggestedRadius: radiusInMeters,
        displayWkt: wkt,
        isCircleGeometry: true, // Flag to indicate this comes from circle tool
      };
    } catch (error) {
      console.error("Error processing GEOMETRYCOLLECTION:", error);
      return {
        isValid: false,
        isCircleGeometry: false,
        isPolygon: false,
        wkt: "",
        error: "Error processing circle geometry",
      };
    }
  }

  /**
   * Calculate radius in meters from LINESTRING coordinates
   * @param lineStringCoords String containing the LINESTRING coordinates
   * @returns Radius in meters or null if calculation fails
   */
  private calculateRadiusFromLineString(lineStringCoords: string): number | null {
    try {
      // Parse coordinates: "lon1 lat1,lon2 lat2"
      const points = lineStringCoords.split(",").map((point) => {
        const [lon, lat] = point.trim().split(" ").map(Number);
        return { lon, lat };
      });

      if (points.length !== 2) {
        console.error("LINESTRING should contain exactly 2 points for radius calculation");
        return null;
      }

      const [point1, point2] = points;

      // Calculate distance using Haversine formula for geographic coordinates
      const radiusInMeters = this.calculateHaversineDistance(point1.lat, point1.lon, point2.lat, point2.lon);
      return Math.round(radiusInMeters);
    } catch (error) {
      console.error("Error calculating radius from LINESTRING:", error);
      return null;
    }
  }

  /**
   * Calculate distance between two geographic points using Haversine formula
   * @param lat1 Latitude of first point
   * @param lon1 Longitude of first point
   * @param lat2 Latitude of second point
   * @param lon2 Longitude of second point
   * @returns Distance in meters
   * todo: replace by opensearch distance
   */
  private calculateHaversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371000; // Earth's radius in meters
    const dLat = this.degreesToRadians(lat2 - lat1);
    const dLon = this.degreesToRadians(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.degreesToRadians(lat1)) *
        Math.cos(this.degreesToRadians(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Convert degrees to radians
   * @param degrees Angle in degrees
   * //todo: crear en geoservice una funcion generica creo que coremap tiene funciones
   * @returns Angle in radians
   */
  private degreesToRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Simple WKT detection
   * //todo: crear en geoservice una funcion generica
   */
  private isWKT(text: string): boolean {
    const wktKeywords = [
      "POINT",
      "LINESTRING",
      "POLYGON",
      "MULTIPOINT",
      "MULTILINESTRING",
      "MULTIPOLYGON",
      "GEOMETRYCOLLECTION",
    ];
    const upperText = text.toUpperCase().trim();
    return wktKeywords.some((keyword) => upperText.startsWith(keyword));
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    const related = event.relatedTarget as Node | null;
    if (related && (event.currentTarget as HTMLElement).contains(related)) {
      return;
    }
    this.isDragOver = false;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  /**
   * Handle file selection from input
   */
  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
    }
    input.value = "";
  }

  /**
   * Process uploaded file
   */
  private async handleFile(file: File): Promise<void> {
    this.fileError = null;
    this.fileProcessingState = "processing";
    this.uploadedFile = file;

    // Validate file type
    const allowedTypes = this.config.allowedFileTypes || this.DEFAULT_ALLOWED_TYPES;
    const fileExt = "." + FileReaderService.getFileExtension(file.name);
    if (!allowedTypes.includes(fileExt)) {
      this.fileError = `File type not supported. Allowed: ${allowedTypes.join(", ")}`;
      this.fileProcessingState = "error";
      this.cdr.detectChanges();
      return;
    }

    // Validate file size
    const maxSize = this.config.maxFileSize || this.DEFAULT_MAX_FILE_SIZE;
    if (file.size > maxSize) {
      this.fileError = `File too large. Maximum size: ${this.maxFileSizeFormatted}`;
      this.fileProcessingState = "error";
      this.cdr.detectChanges();
      return;
    }

    const ext = FileReaderService.getFileExtension(file.name).toLowerCase();

    // Binary / structured formats — delegate parsing to FileReaderService
    if (["kml", "kmz", "zip", "csv"].includes(ext)) {
      this.fileReaderService.readFile(file).subscribe({
        next: (result) => {
          if (result.status === "error" || !result.data?.features?.length) {
            this.fileError = result.errors?.[0]?.message || "Could not read file geometry.";
            this.fileProcessingState = "error";
          } else {
            const wkt =
              ext === "csv"
                ? this.featuresToMultiPointWkt(result.data.features)
                : this.featureToPolygonWkt(result.data.features[0]);
            if (!wkt) {
              this.fileError = "Could not extract geometry from file.";
              this.fileProcessingState = "error";
            } else {
              this.fileGeometry = {
                isValid: true,
                isPolygon: true,
                isCircleGeometry: false,
                wkt,
              };
              this.fileProcessingState = "success";
            }
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.fileError = typeof err === "string" ? err : "Error reading file";
          this.fileProcessingState = "error";
          this.cdr.detectChanges();
        },
      });
      return;
    }

    // Text-based formats (WKT, GeoJSON) — original flow
    try {
      const fileContent = await this.readFileContent(file);
      const result = await this.processGeometryText(fileContent);

      if (result.isValid) {
        this.fileGeometry = result;
        if (result.isCircleGeometry && result.suggestedRadius) {
          this.radiusForm.patchValue({ radius: result.suggestedRadius });
          this.radiusForm.get("radius").markAsTouched();
          this.radiusForm.get("radius").updateValueAndValidity();
        }
        this.fileProcessingState = "success";
      } else {
        this.fileError = result.error || "Invalid geometry in file";
        this.fileProcessingState = "error";
      }
    } catch (error) {
      this.fileError = "Error reading file";
      this.fileProcessingState = "error";
    }

    this.cdr.detectChanges();
  }

  /** Converts a (Mercator) polygon feature to WGS84 polygon WKT. */
  private featureToPolygonWkt(feature: unknown): string | null {
    try {
      const wgs84 = GeoService.toWgs84(feature);
      const geometry = (wgs84 as { geometry?: unknown }).geometry ?? wgs84;
      return GeoService.geoJSONFeatureToWKT(geometry) as string;
    } catch (e) {
      console.error("featureToPolygonWkt failed:", e);
      return null;
    }
  }

  /** Converts (Mercator) point features to a WGS84 MULTIPOINT WKT. */
  private featuresToMultiPointWkt(features: unknown[]): string | null {
    const points: string[] = [];
    for (const f of features) {
      try {
        const wgs84 = GeoService.toWgs84(f) as { geometry?: { coordinates?: unknown } };
        const coords = wgs84.geometry?.coordinates;
        if (Array.isArray(coords) && typeof coords[0] === "number" && typeof coords[1] === "number") {
          points.push(`${coords[0]} ${coords[1]}`);
        }
      } catch (e) {
        console.error("featuresToMultiPointWkt failed on feature:", e);
      }
    }
    return points.length ? `MULTIPOINT(${points.join(",")})` : null;
  }

  /**
   * Read file content as text
   */
  private readFileContent(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsText(file);
    });
  }

  /**
   * Remove uploaded file
   */
  public removeFile(event: Event): void {
    event.stopPropagation();
    this.uploadedFile = null;
    this.fileError = null;
    this.fileProcessingState = "idle";
    this.fileGeometry = null; // Only clear file-specific geometry
    this.cdr.detectChanges();
  }

  /**
   * Format file size for display
   */
  public formatFileSize(bytes: number): string {
    if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    } else if (bytes >= 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${bytes} bytes`;
  }

  /**
   * Select map tool
   */
  public selectMapTool(tool: MapSelectionType): void {
    this.selectedMapTool = tool;
    this.cdr.detectChanges();
  }

  /**
   * Start map selection
   */
  public startMapSelection(): void {
    if (!this.selectedMapTool) {
      return;
    }

    // Generate unique request ID for geofencing
    const requestId = `geofencing-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Create map selection request
    const mapSelectionRequest: MapSelectionRequest = {
      id: requestId,
      type: this.selectedMapTool,
      data: {
        context: "geofencing",
        tool: this.selectedMapTool,
      },
    };

    // Close dialog with map selection request
    const result: GeofencingDialogResult = {
      success: false,
      inputMethod: GeometryInputMethod.MAP,
      mapSelectionRequest: mapSelectionRequest,
    };

    this.dialogRef.close(result);
  }

  /**
   * Get map tool label
   */
  public getMapToolLabel(tool: MapSelectionType | null): string {
    switch (tool) {
      case MapSelectionType.CIRCLE:
        return "Point";
      case MapSelectionType.SQUARE:
        return "Rectangle";
      case MapSelectionType.POLYGON:
        return "Polygon";
      default:
        return "";
    }
  }

  /**
   * Clear all errors and reset state
   */
  private clearErrors(): void {
    this.fileError = null;
    this.globalError = null;
    this.radiusForm.get("radius")?.enable(); // Re-enable radius field
  }

  /**
   * Handle cancel action
   */
  public onCancel(): void {
    this.dialogRef.close();
  }

  /**
   * Handle apply action
   */
  public onDelete(): void {
    const result = {
      success: false,
      delete: true,
    };
    this.dialogRef.close(result);
  }

  /**
   * Handle apply action
   */
  public onApply(): void {
    if (!this.canApplyGeofencing || !this.currentGeometry?.wkt) {
      return;
    }

    let result: GeofencingDialogResult;
    let radius: number;

    if (this.currentGeometry.isCircleGeometry) {
      // Point + radius result
      radius = this.radiusForm.get("radius")?.value || this.DEFAULT_RADIUS_METERS;
      result = {
        success: true,
        inputMethod: this.currentInputMethod,
        type: GeometryType.RADIUS,
        radiusMeters: radius,
        location: this.currentGeometry.wkt,
        wkt: this.currentGeometry.wkt, // Always provide WKT for backend
        geometryType: "circle", // Simplified type indicator
        displayWkt: this.currentGeometry.displayWkt,
      };
    } else {
      // Polygon result
      result = {
        success: true,
        inputMethod: this.currentInputMethod,
        type: GeometryType.POLYGON,
        wkt: this.currentGeometry.wkt,
        geometryType: "polygon", // Simplified type indicator
        displayWkt: this.currentGeometry.wkt,
      };
    }

    if (this.enforceAreaLimit) {
      this.applyGeofencingSearch(result);
    } else {
      // Not an embeddings search context (e.g. custom-elements detection area) — nothing to
      // warm up, just return the geometry.
      this.dialogRef.close(result);
    }
  }

  /**
   * Warms up the embeddings cache for the geometry via `SearchRequestService.validateGeometry()`.
   * The dialog closes with the result as soon as EITHER the warm-up settles OR
   * {@link APPLY_MAX_WAIT_MS} elapses, whichever comes first — the cache warm-up is a
   * best-effort optimization, not a requirement, so a slow/hanging call must never block the
   * dialog. If the timeout wins the race, the warm-up request keeps running in the background
   * (it isn't cancelled) so the cache still benefits once it eventually resolves.
   */
  private applyGeofencingSearch(result: GeofencingDialogResult): void {
    this.globalError = null;
    this.isProcessing = true;
    this.cdr.detectChanges();

    let closed = false;
    const closeOnce = () => {
      if (closed) return;
      closed = true;
      this.isProcessing = false;
      this.dialogRef.close(result);
    };

    this.searchRequestService
      .validateGeometry(result.wkt, result.radiusMeters)
      .pipe(
        catchError((error) => {
          console.error("Failed to cache embeddings from geofencing:", error);
          return of(null);
        }),
      )
      .subscribe(closeOnce);

    timer(this.APPLY_MAX_WAIT_MS).subscribe(closeOnce);
  }

  /**
   * Get current active geometry based on the active method (visibility-aware).
   */
  get currentGeometry(): ProcessedGeometry | null {
    switch (this.currentInputMethod) {
      case GeometryInputMethod.MAP:
        return this.mapGeometry;
      case GeometryInputMethod.FILE:
        return this.fileGeometry;
      case GeometryInputMethod.TEXT:
        return this.textGeometry;
      case GeometryInputMethod.LAYER:
        return this.layerGeometry;
      default:
        return null;
    }
  }

  /**
   * Get uppercased file extension from a layer name, e.g. "GEOJSON"
   */
  public getLayerFileExt(name: string): string {
    const dot = name.lastIndexOf(".");
    return dot >= 0 ? name.slice(dot + 1).toUpperCase() : "";
  }

  /**
   * Select a My Data layer and load its WKT as the geofencing geometry
   */
  public onLayerSelected(layer: IGeofencingLayer): void {
    this.selectedLayerId = layer.id;
    this.layerGeometry = null;
    this.processGeometryText(layer.wkt).then((result) => {
      this.layerGeometry = result;
      this.cdr.detectChanges();
    });
  }

  /**
   * Geofencing validation of the geometry selected in the active tab: measures its area and
   * checks it against the maximum the embeddings API can process.
   */
  get geometryValidation(): IGeofencingSearchValidation {
    const geometry = this.currentGeometry;
    const radiusMeters = geometry?.isCircleGeometry ? this.radiusForm.get("radius")?.value : null;
    const key = `${geometry?.wkt ?? ""}|${radiusMeters ?? ""}`;

    if (key !== this.measuredGeometryKey) {
      this.measuredGeometryKey = key;
      this.measuredGeometryValidation = this.enforceAreaLimit
        ? GeofencingValidationService.validateGeometry({ wkt: geometry?.wkt, radiusMeters })
        : { valid: true, reason: null, area: null };
    }

    return this.measuredGeometryValidation;
  }

  /**
   * Measured area of the geometry selected in the active tab, or null when it has no
   * measurable area (e.g. a set of points imported from a CSV).
   */
  get geometryArea(): IGeofencingArea | null {
    return this.geometryValidation.area;
  }

  /**
   * Whether the selected geometry covers more area than the embeddings API can process.
   * Always false when {@link enforceAreaLimit} is off — the measured area still reports its
   * own `exceedsLimit` flag (it doesn't know about context), so that's re-checked here.
   */
  get areaExceedsLimit(): boolean {
    return this.enforceAreaLimit && this.geometryArea?.exceedsLimit === true;
  }

  /**
   * Maximum allowed area, formatted for display in hints and error messages.
   */
  get maxAreaLabel(): string {
    return `${MAX_GEOFENCING_AREA_SQUARE_KILOMETERS.toLocaleString("en-US")} km²`;
  }

  /**
   * Check if current geometry is valid for applying filter
   */
  get canApplyGeofencing(): boolean {
    const geometry = this.currentGeometry;
    if (!geometry?.isValid) {
      return false;
    }

    // An over-limit area is rejected by the embeddings API, so it must not be applicable at
    // all — but only when this dialog instance is actually gated by that limit.
    if (this.enforceAreaLimit && this.areaExceedsLimit) {
      return false;
    }

    if (geometry.isCircleGeometry) {
      return this.radiusForm.valid;
    }

    return geometry.isPolygon;
  }
}
